import type { Localized } from '../types/notice'
import type { ChannelsState, AudienceState } from '../types/composer'
import { CATEGORIES, SECTORS } from '../data/communications'

/** What ATNA extracted from the free-text brief, refined by the answers. */
export interface AtnaUnderstanding {
  /** Raw brief as typed by the author. */
  brief: string
  category: string
  categoryLabel: Localized
  /** Short notice title inferred from the brief. */
  title: string
  /** When the situation applies, as a human phrase ("le 12 mars, dès 8 h"). */
  when: string
  /** Where it applies — sector ids, or empty for the whole city. */
  sectorIds: string[]
  /** Free-text location when the author named a street rather than a sector. */
  place: string
  /** Channels ATNA recommends for this kind of notice. */
  channels: (keyof ChannelsState)[]
  /** True for situations needing the fastest channels. */
  urgent: boolean
}

// --- INTERPRETATION ---------------------------------------------------------

/** Keyword sets used to classify the brief into an existing category. */
const CATEGORY_HINTS: { category: string; words: string[] }[] = [
  {
    category: 'eau',
    words: [
      'eau', 'aqueduc', 'ébullition', 'ebullition', 'bouillir', 'water', 'boil',
      'pression', 'robinet',
    ],
  },
  {
    category: 'travaux',
    words: [
      'travaux', 'chantier', 'rue', 'route', 'asphalte', 'chaussée', 'chaussee',
      'fermeture', 'fermée', 'fermee', 'détour', 'detour', 'entrave', 'trottoir',
      'work', 'closure', 'road',
    ],
  },
  {
    category: 'collecte',
    words: [
      'collecte', 'ordures', 'recyclage', 'compost', 'déchets', 'dechets',
      'bac', 'collection', 'waste', 'garbage',
    ],
  },
  {
    category: 'urgence',
    words: [
      'urgence', 'alerte', 'évacuation', 'evacuation', 'incendie', 'sinistre',
      'tempête', 'tempete', 'inondation', 'panne', 'emergency', 'alert', 'flood',
    ],
  },
  {
    category: 'loisirs',
    words: ['loisirs', 'piscine', 'patinoire', 'aréna', 'arena', 'camp', 'inscription', 'parc'],
  },
  {
    category: 'biblio',
    words: ['bibliothèque', 'bibliotheque', 'library', 'livre'],
  },
  {
    category: 'permis',
    words: ['permis', 'permit', 'rénovation', 'renovation', 'construction'],
  },
  {
    category: 'taxes',
    words: ['taxes', 'taxe', 'compte de taxes', 'versement', 'tax'],
  },
  {
    category: 'evenement',
    words: [
      'événement', 'evenement', 'fête', 'fete', 'festival', 'spectacle',
      'event', 'celebration', 'marché', 'marche',
    ],
  },
]

const DATE_TIME_PATTERN =
  /((le\s+)?\d{1,2}\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)[^.,;]*)|(\d{1,2}\s?h(\s?\d{2})?)|(\d{1,2}:\d{2})|(aujourd['’]hui)|(demain)|(ce soir)|(cette semaine)|(dès\s+\w+)|(jusqu['’]à[^.,;]*)/gi

/** Sentence-cased, punctuation-trimmed title of at most ~70 characters. */
function toTitle(text: string): string {
  const firstClause = text
    .split(/[.!?\n]/)[0]
    .replace(/^(bonjour|salut|svp|s'il vous plaît)[,\s]*/i, '')
    .trim()
  const clipped =
    firstClause.length > 70
      ? `${firstClause.slice(0, 69).replace(/\s+\S*$/, '')}…`
      : firstClause
  return clipped.charAt(0).toUpperCase() + clipped.slice(1)
}

/** Classifies a free-text brief and pre-fills everything it can infer. */
export function interpretBrief(brief: string): AtnaUnderstanding {
  const lower = brief.toLowerCase()

  const scored = CATEGORY_HINTS.map((hint) => ({
    category: hint.category,
    score: hint.words.filter((w) => lower.includes(w)).length,
  })).sort((a, b) => b.score - a.score)

  const category = scored[0]?.score > 0 ? scored[0].category : 'info'
  const option = CATEGORIES.find((c) => c.value === category)
  const urgent = Boolean(option?.urgent)

  const when = Array.from(brief.matchAll(DATE_TIME_PATTERN))
    .map((m) => m[0].trim())
    .slice(0, 2)
    .join(', ')

  const sectorIds = SECTORS.filter((s) =>
    s.label.fr
      .replace('Secteurs ', '')
      .split('-')
      .some((letter) =>
        new RegExp(`secteur\\s+${letter.toLowerCase()}\\b`, 'i').test(lower),
      ),
  ).map((s) => s.id)

  const placeMatch = brief.match(
    /(?:rue|boulevard|avenue|chemin|montée|montee|place)\s+[A-ZÀ-Ÿ][\wÀ-ÿ'’-]*(\s+[A-ZÀ-Ÿ][\wÀ-ÿ'’-]*)?/,
  )

  const channels: (keyof ChannelsState)[] = urgent
    ? ['push', 'sms', 'email', 'voice', 'website']
    : category === 'evenement' || category === 'loisirs'
      ? ['push', 'email', 'social', 'website']
      : ['push', 'email', 'website']

  return {
    brief: brief.trim(),
    category,
    categoryLabel: option?.label ?? { fr: 'Information générale', en: 'General information' },
    title: toTitle(brief),
    when,
    sectorIds,
    place: placeMatch?.[0] ?? '',
    channels,
    urgent,
  }
}

// --- CLARIFYING QUESTIONS ---------------------------------------------------

export type AtnaQuestionId = 'when' | 'where' | 'channels' | 'followup'

export interface AtnaOption {
  label: Localized
  value: string
}

export interface AtnaQuestion {
  id: AtnaQuestionId
  /** Card heading, e.g. "Quand cela s'applique-t-il ?". */
  prompt: Localized
  /** Value ATNA already believes, shown in bold on the card. */
  guess?: string
  /** Caption under the guess explaining where it came from. */
  guessHint?: Localized
  options: AtnaOption[]
  /** Lets the author answer in their own words instead of picking an option. */
  freeText?: Localized
}

/** The gaps ATNA needs filled before it can write the notice. */
export function buildQuestions(u: AtnaUnderstanding): AtnaQuestion[] {
  const questions: AtnaQuestion[] = []

  if (!u.when) {
    questions.push({
      id: 'when',
      prompt: {
        fr: 'Quand cela s’applique-t-il ?',
        en: 'When does this apply?',
      },
      options: [
        { label: { fr: 'Aujourd’hui', en: 'Today' }, value: 'today' },
        { label: { fr: 'Demain', en: 'Tomorrow' }, value: 'tomorrow' },
        { label: { fr: 'Jusqu’à nouvel ordre', en: 'Until further notice' }, value: 'ongoing' },
      ],
      freeText: {
        fr: 'Préciser une date ou une heure…',
        en: 'Specify a date or time…',
      },
    })
  }

  if (u.sectorIds.length === 0) {
    questions.push({
      id: 'where',
      prompt: { fr: 'Qui souhaitez-vous joindre ?', en: 'Who do you want to reach?' },
      guess: u.place || undefined,
      guessHint: u.place
        ? { fr: 'Lieu détecté dans votre demande', en: 'Location detected in your request' }
        : undefined,
      options: [
        { label: { fr: 'Toute la ville', en: 'The whole city' }, value: 'org' },
        ...SECTORS.map((s) => ({ label: s.label, value: s.id })),
      ],
    })
  }

  questions.push({
    id: 'channels',
    prompt: { fr: 'Sur quels canaux diffuser ?', en: 'Which channels should carry it?' },
    guess: u.channels.length
      ? u.channels
          .map((c) => CHANNEL_NAMES[c].fr)
          .join(', ')
      : undefined,
    guessHint: u.urgent
      ? {
          fr: 'Recommandation pour un avis urgent',
          en: 'Recommended for an urgent notice',
        }
      : { fr: 'Recommandation pour cette catégorie', en: 'Recommended for this category' },
    options: [
      { label: { fr: 'Oui, ces canaux', en: 'Yes, these channels' }, value: 'accept' },
      { label: { fr: 'Tous les canaux', en: 'Every channel' }, value: 'all' },
      { label: { fr: 'Courriel seulement', en: 'Email only' }, value: 'email' },
    ],
  })

  return questions
}

export const CHANNEL_NAMES: Record<keyof ChannelsState, Localized> = {
  email: { fr: 'Courriel', en: 'Email' },
  sms: { fr: 'SMS', en: 'SMS' },
  voice: { fr: 'Appel vocal', en: 'Voice call' },
  push: { fr: 'Notification', en: 'Push' },
  website: { fr: 'Site web', en: 'Website' },
  social: { fr: 'Réseaux sociaux', en: 'Social media' },
}

/** Folds an answer back into the understanding. */
export function applyAnswer(
  u: AtnaUnderstanding,
  questionId: AtnaQuestionId,
  value: string,
  lang: 'fr' | 'en',
): AtnaUnderstanding {
  const fr = lang === 'fr'
  switch (questionId) {
    case 'when': {
      const when =
        value === 'today'
          ? fr ? 'aujourd’hui' : 'today'
          : value === 'tomorrow'
            ? fr ? 'demain' : 'tomorrow'
            : value === 'ongoing'
              ? fr ? 'jusqu’à nouvel ordre' : 'until further notice'
              : value
      return { ...u, when }
    }
    case 'where':
      return value === 'org'
        ? { ...u, sectorIds: [] }
        : { ...u, sectorIds: [value] }
    case 'channels':
      return {
        ...u,
        channels:
          value === 'all'
            ? ['email', 'sms', 'voice', 'push', 'website', 'social']
            : value === 'email'
              ? ['email']
              : u.channels,
      }
    default:
      return u
  }
}

// --- NOTICE GENERATION ------------------------------------------------------

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function paragraphs(...lines: string[]): string {
  return lines.filter(Boolean).map((l) => `<p>${escapeHtml(l)}</p>`).join('')
}

function clamp(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trim()}…`
}

export interface AtnaDraft {
  title: string
  category: string
  channels: ChannelsState
  audience: Partial<AudienceState>
}

/**
 * Writes the complete notice from the understanding: every recommended channel
 * gets content adapted to its own format and length constraints.
 */
export function buildNotice(
  u: AtnaUnderstanding,
  base: ChannelsState,
  contentLang: 'fr' | 'en',
): AtnaDraft {
  const fr = contentLang === 'fr'
  const link = 'brossard.ca/avis'
  const scope =
    u.sectorIds.length > 0
      ? SECTORS.filter((s) => u.sectorIds.includes(s.id))
          .map((s) => s.label[contentLang])
          .join(', ')
      : u.place || (fr ? 'l’ensemble de la ville' : 'the whole city')

  const situation = u.brief.charAt(0).toUpperCase() + u.brief.slice(1)
  const whenPhrase = u.when
    ? fr
      ? `En vigueur ${u.when}.`
      : `In effect ${u.when}.`
    : ''
  const wherePhrase = fr ? `Secteur visé : ${scope}.` : `Affected area: ${scope}.`

  const active = new Set(u.channels)
  const channels: ChannelsState = {
    email: {
      ...base.email,
      active: active.has('email'),
      subject: active.has('email') ? u.title : base.email.subject,
      body: active.has('email')
        ? paragraphs(
            fr ? 'Chers résidents,' : 'Dear residents,',
            situation,
            whenPhrase,
            wherePhrase,
            fr
              ? `Pour les détails et les mises à jour, consultez ${link}.`
              : `For details and updates, visit ${link}.`,
            fr ? 'Merci de votre collaboration.' : 'Thank you for your cooperation.',
            fr ? 'Ville de Brossard' : 'City of Brossard',
          )
        : base.email.body,
    },
    sms: {
      ...base.sms,
      active: active.has('sms'),
      text: active.has('sms')
        ? `<p>${escapeHtml(
            clamp(
              `${u.title}. ${whenPhrase} ${fr ? 'Infos :' : 'Info:'} ${link}`.replace(
                /\s+/g,
                ' ',
              ),
              158,
            ),
          )}</p>`
        : base.sms.text,
    },
    voice: {
      ...base.voice,
      active: active.has('voice'),
      script: active.has('voice')
        ? `<p>${escapeHtml(
            fr
              ? `Bonjour, ceci est un message automatisé de la Ville de Brossard. ${situation} ${whenPhrase} ${wherePhrase} Pour plus d'information, consultez le site ${link}. Merci.`
              : `Hello, this is an automated message from the City of Brossard. ${situation} ${whenPhrase} ${wherePhrase} For more information, visit ${link}. Thank you.`,
          )}</p>`
        : base.voice.script,
    },
    push: {
      ...base.push,
      active: active.has('push'),
      title: active.has('push')
        ? `<p>${escapeHtml(clamp(u.title, 48))}</p>`
        : base.push.title,
      message: active.has('push')
        ? `<p>${escapeHtml(
            clamp(whenPhrase ? `${whenPhrase} ${scope}` : situation, 98),
          )}</p>`
        : base.push.message,
    },
    website: {
      ...base.website,
      active: active.has('website'),
      bannerText: active.has('website')
        ? `<p>${escapeHtml(u.title)}</p>`
        : base.website.bannerText,
      ctaLabel: active.has('website')
        ? `<p>${escapeHtml(fr ? 'En savoir plus' : 'Learn more')}</p>`
        : base.website.ctaLabel,
    },
    social: {
      ...base.social,
      active: active.has('social'),
      postText: active.has('social')
        ? paragraphs(
            `📢 ${u.title}`,
            situation,
            whenPhrase,
            fr ? `Détails : ${link}` : `Details: ${link}`,
            '#Brossard #InfoCitoyen',
          )
        : base.social.postText,
    },
  }

  return {
    title: u.title,
    category: u.category,
    channels,
    audience:
      u.sectorIds.length > 0
        ? { zone: 'sector', selectedSectors: u.sectorIds }
        : { zone: 'org', selectedSectors: [] },
  }
}
